import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './Calendar.css';

const STATUS_COLORS = {
  confirmed: { bg:'rgba(74,222,128,0.15)', border:'#4ade80', text:'#4ade80' },
  pending:   { bg:'rgba(250,204,21,0.15)',  border:'#facc15', text:'#facc15' },
  cancelled: { bg:'rgba(248,113,113,0.15)', border:'#f87171', text:'#f87171' },
  completed: { bg:'rgba(96,165,250,0.15)',  border:'#60a5fa', text:'#60a5fa' },
  no_show:   { bg:'rgba(160,160,160,0.15)', border:'#a0a0a0', text:'#a0a0a0' },
};

const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const formatTime = t => {
  if (!t) return '';
  const [h,m] = t.split(':');
  const hour = parseInt(h);
  return `${hour>12?hour-12:hour||12}:${m}${hour>=12?'pm':'am'}`;
};

const toDateStr = d => {
  const year = d.getFullYear();
  const month = String(d.getMonth()+1).padStart(2,'0');
  const day = String(d.getDate()).padStart(2,'0');
  return `${year}-${month}-${day}`;
};

export default function Calendar({ apiEndpoint, onSessionClick, role='admin' }) {
  const [view, setView] = useState('week');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [sessions, setSessions] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hoveredSession, setHoveredSession] = useState(null);
  const [overflowDay, setOverflowDay] = useState(null);

  const getDateRange = useCallback(() => {
    if (view === 'month') {
      const start = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const end = new Date(currentDate.getFullYear(), currentDate.getMonth()+1, 0);
      return { start: toDateStr(start), end: toDateStr(end) };
    } else {
      const day = currentDate.getDay();
      const start = new Date(currentDate);
      start.setDate(currentDate.getDate() - day);
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      return { start: toDateStr(start), end: toDateStr(end) };
    }
  }, [currentDate, view]);

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    try {
      const { start, end } = getDateRange();
      const res = await axios.get(apiEndpoint, { params: { start, end } });
      setSessions(res.data.sessions || []);
      setAvailability(res.data.availability || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [apiEndpoint, getDateRange]);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  const navigate = dir => {
    const d = new Date(currentDate);
    if (view === 'month') d.setMonth(d.getMonth() + dir);
    else d.setDate(d.getDate() + (dir * 7));
    setCurrentDate(d);
  };

  const getSessionsForDate = dateStr =>
    sessions.filter(s => s.session_date.split('T')[0] === dateStr);

  const getAvailabilityForDate = dateStr =>
    availability.filter(a => a.date === dateStr);

  const today = toDateStr(new Date());

  const renderMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month+1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);
    return (
      <div className="cal-month">
        <div className="cal-weekdays">{DAYS.map(d => <div key={d} className="cal-weekday">{d}</div>)}</div>
        <div className="cal-grid">
          {cells.map((day, i) => {
            if (!day) return <div key={'e'+i} className="cal-cell cal-cell--empty" />;
            const dateStr = year+'-'+String(month+1).padStart(2,'0')+'-'+String(day).padStart(2,'0');
            const daySessions = getSessionsForDate(dateStr);
            const dayAvail = getAvailabilityForDate(dateStr);
            const isToday = dateStr === today;
            return (
              <div key={dateStr} className={'cal-cell'+(isToday?' cal-cell--today':'')+(dayAvail.length>0?' cal-cell--available':'')}>
                <div className="cal-cell-header">
                  <span className={'cal-day-num'+(isToday?' cal-day-num--today':'')}>{day}</span>
                  {dayAvail.length > 0 && (
                    <span className="cal-avail-badge" title={dayAvail.map(a => formatTime(a.start_time)+'-'+formatTime(a.end_time)).join(', ')}>
                      {dayAvail.length === 1 ? formatTime(dayAvail[0].start_time) : dayAvail.length+' slots'}
                    </span>
                  )}
                </div>
                <div className="cal-cell-sessions">
                  {daySessions.slice(0,3).map(s => {
                    const c = STATUS_COLORS[s.status] || STATUS_COLORS.pending;
                    return (
                      <div key={s.id} className="cal-session-pill"
                        style={{ background:c.bg, borderLeft:'3px solid '+c.border, color:c.text }}
                        onClick={() => onSessionClick && onSessionClick(s)}
                        onMouseEnter={() => setHoveredSession(s)}
                        onMouseLeave={() => setHoveredSession(null)}>
                        <span className="cal-session-time">{formatTime(s.start_time)}</span>
                        <span className="cal-session-name">
                          {role==='admin' ? s.instructor_first+' '+s.instructor_last : s.parent_first+' '+s.parent_last}
                        </span>
                      </div>
                    );
                  })}
                  {daySessions.length > 3 && (
                    <div className="cal-session-more" onClick={e => { e.stopPropagation(); setOverflowDay({ dateStr, sessions: daySessions }); }}>
                      +{daySessions.length-3} more
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderWeek = () => {
    const day = currentDate.getDay();
    const weekStart = new Date(currentDate);
    weekStart.setDate(currentDate.getDate() - day);
    const weekDays = Array.from({length:7}, (_,i) => {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      return d;
    });
    return (
      <div className="cal-week">
        <div className="cal-week-header">
          {weekDays.map((d,i) => {
            const dateStr = toDateStr(d);
            const isToday = dateStr === today;
            return (
              <div key={i} className={'cal-week-day-header'+(isToday?' cal-week-day-header--today':'')}>
                <div className="cal-week-day-name">{DAYS[i]}</div>
                <div className={'cal-week-day-num'+(isToday?' cal-week-day-num--today':'')}>{d.getDate()}</div>
              </div>
            );
          })}
        </div>
        <div className="cal-week-body">
          {weekDays.map((d,i) => {
            const dateStr = toDateStr(d);
            const daySessions = getSessionsForDate(dateStr);
            const dayAvail = getAvailabilityForDate(dateStr);
            const isToday = dateStr === today;
            return (
              <div key={i} className={'cal-week-col'+(isToday?' cal-week-col--today':'')}>
                {dayAvail.map((a, ai) => (
                  <div key={'avail-'+ai} className="cal-avail-block">
                    <span className="cal-avail-icon">◷</span>
                    {formatTime(a.start_time)}–{formatTime(a.end_time)}
                  </div>
                ))}
                {daySessions.length === 0 && dayAvail.length === 0
                  ? <div className="cal-week-empty">-</div>
                  : <>
                    {daySessions.slice(0,3).map(s => {
                      const c = STATUS_COLORS[s.status] || STATUS_COLORS.pending;
                      return (
                        <div key={s.id} className="cal-week-session"
                          style={{ background:c.bg, borderLeft:'3px solid '+c.border }}
                          onClick={() => onSessionClick && onSessionClick(s)}
                          onMouseEnter={() => setHoveredSession(s)}
                          onMouseLeave={() => setHoveredSession(null)}>
                          <div className="cal-week-session-time" style={{color:c.text}}>{formatTime(s.start_time)} - {formatTime(s.end_time)}</div>
                          <div className="cal-week-session-name">{role==='admin' ? s.instructor_first+' '+s.instructor_last : s.parent_first+' '+s.parent_last}</div>
                          {s.player_first && <div className="cal-week-session-player">{s.player_first} {s.player_last}</div>}
                          <div className="cal-week-session-status" style={{color:c.text}}>{s.status}</div>
                        </div>
                      );
                    })}
                    {daySessions.length > 3 && (
                      <div className="cal-session-more"
                        onClick={e => { e.stopPropagation(); setOverflowDay({ dateStr, sessions: daySessions }); }}>
                        +{daySessions.length-3} more
                      </div>
                    )}
                  </>
                }
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const title = view === 'month'
    ? MONTHS[currentDate.getMonth()]+' '+currentDate.getFullYear()
    : (() => {
        const day = currentDate.getDay();
        const start = new Date(currentDate); start.setDate(currentDate.getDate()-day);
        const end = new Date(start); end.setDate(start.getDate()+6);
        return MONTHS[start.getMonth()]+' '+start.getDate()+' - '+MONTHS[end.getMonth()]+' '+end.getDate()+', '+end.getFullYear();
      })();

  return (
    <div className="cal-container">
      <div className="cal-toolbar">
        <div className="cal-toolbar-left">
          <button className="cal-nav-btn" onClick={() => navigate(-1)}>&#8249;</button>
          <button className="cal-today-btn" onClick={() => setCurrentDate(new Date())}>Today</button>
          <button className="cal-nav-btn" onClick={() => navigate(1)}>&#8250;</button>
          <span className="cal-title">{title}</span>
          {loading && <span className="cal-loading">Loading...</span>}
        </div>
        <div className="cal-toolbar-right">
          <div className="cal-view-toggle">
            <button className={'cal-view-btn'+(view==='month'?' active':'')} onClick={() => setView('month')}>Month</button>
            <button className={'cal-view-btn'+(view==='week'?' active':'')} onClick={() => setView('week')}>Week</button>
          </div>
        </div>
      </div>
      <div className="cal-legend">
        {availability.length > 0 && (
          <div className="cal-legend-item">
            <div className="cal-legend-dot" style={{background:'#34d399'}} />
            <span className="cal-legend-label">available</span>
          </div>
        )}
        {Object.entries(STATUS_COLORS).map(([status, colors]) => (
          <div key={status} className="cal-legend-item">
            <div className="cal-legend-dot" style={{background:colors.border}} />
            <span className="cal-legend-label">{status}</span>
          </div>
        ))}
      </div>
      {view === 'month' ? renderMonth() : renderWeek()}
      {overflowDay && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setOverflowDay(null)}>
          <div className="modal" style={{ maxWidth:'400px', width:'90%' }}>
            <div className="modal-header">
              <span className="modal-title">{(() => { const [y,m,d] = overflowDay.dateStr.split('-'); return new Date(+y,+m-1,+d).toLocaleDateString('en-US',{weekday:'long',month:'long',day:'numeric'}); })()}</span>
              <button className="modal-close" onClick={() => setOverflowDay(null)}>x</button>
            </div>
            <div className="modal-body" style={{ display:'flex', flexDirection:'column', gap:'8px', maxHeight:'60vh', overflowY:'auto' }}>
              {overflowDay.sessions.map(s => {
                const c = STATUS_COLORS[s.status] || STATUS_COLORS.pending;
                return (
                  <div key={s.id} style={{ background:c.bg, borderLeft:'3px solid '+c.border, borderRadius:'6px', padding:'10px 14px', cursor:'pointer' }}
                    onClick={() => { setOverflowDay(null); onSessionClick && onSessionClick(s); }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'4px' }}>
                      <span style={{ fontSize:'0.85rem', fontWeight:'600', color:c.text }}>{formatTime(s.start_time)} – {formatTime(s.end_time)}</span>
                      <span style={{ fontSize:'0.75rem', color:c.text, textTransform:'capitalize' }}>{s.status}</span>
                    </div>
                    <div style={{ fontSize:'0.85rem', color:'var(--text-primary)' }}>
                      {role === 'admin' ? (s.instructor_first+' '+s.instructor_last) : (s.parent_first+' '+s.parent_last)}
                    </div>
                    {s.player_first && <div style={{ fontSize:'0.78rem', color:'var(--text-muted)', marginTop:'2px' }}>{s.player_first} {s.player_last}</div>}
                  </div>
                );
              })}
            </div>
            <div className="modal-footer"><button className="btn btn-outline btn-sm" onClick={() => setOverflowDay(null)}>Close</button></div>
          </div>
        </div>
      )}
      {hoveredSession && (
        <div className="cal-tooltip">
          <div className="cal-tooltip-title">{formatTime(hoveredSession.start_time)} - {formatTime(hoveredSession.end_time)}</div>
          {hoveredSession.instructor_first && <div className="cal-tooltip-row">Instructor: {hoveredSession.instructor_first} {hoveredSession.instructor_last}</div>}
          <div className="cal-tooltip-row">Parent: {hoveredSession.parent_first} {hoveredSession.parent_last}</div>
          {hoveredSession.player_first && <div className="cal-tooltip-row">Player: {hoveredSession.player_first} {hoveredSession.player_last}</div>}
          <div className="cal-tooltip-status" style={{color:(STATUS_COLORS[hoveredSession.status]||STATUS_COLORS.pending).text}}>{hoveredSession.status}</div>
        </div>
      )}
    </div>
  );
}
